import { queryKeys, useHttpClient, useSession } from '@repo/api-client/react';
import { type OrganizationListItem } from '@repo/api-client/dtos';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';

import { deleteJSON } from '../internal/http';
import { syncOrgsFromCache } from './sessionSync';

/**
 * Wire shape returned by `DELETE /v1/orgs/:orgId/forever`. The
 * server only echoes the deleted id; everything else is gone, so a
 * full `Organization` shape would be a lie.
 */
const HardDeleteResponseSchema = z.object({
  id: z.string().min(1),
});

/**
 * Permanently delete an archived organization.
 *
 * One-way: the server cascades through every dependent table
 * (clients, invoices, payments, services, memberships, etc.) so
 * there is no recovery path after this fires. The mobile UI MUST
 * gate this behind a strong danger modal — see
 * `apps/mobile/app/settings/businesses-archived.tsx` for the
 * canonical confirmation flow with a typed-confirm input.
 *
 * Cache reconciliation:
 *
 *   - Drop the row from the archived list cache so it disappears
 *     immediately. (The active list never had it; archive moved
 *     it out before we could reach this point.)
 *   - Drop the detail cache entry so any subscribed screen sees
 *     `undefined` and can route away.
 *
 * Errors are surfaced as the mutation's `error` — the call site is
 * responsible for showing them in our `NoticeSheet`. We deliberately
 * do NOT swallow / rewrite the error here because the call site
 * has more context for the message ("Couldn't delete Acme Studio"
 * vs. a generic "delete failed").
 */
export function useHardDeleteOrganization() {
  const http = useHttpClient();
  const queryClient = useQueryClient();
  const { syncOrganizations } = useSession();

  const mutation = useMutation({
    mutationFn: async (organizationId: string) => {
      const result = await deleteJSON(
        http,
        `/orgs/${organizationId}/forever`,
        HardDeleteResponseSchema,
      );
      return result.id;
    },
    onSuccess: (deletedId: string) => {
      queryClient.removeQueries({
        queryKey: queryKeys.organizations.detail(deletedId),
      });
      queryClient.setQueryData<{ items: OrganizationListItem[] }>(
        queryKeys.organizations.archived(),
        (cache) =>
          cache
            ? { ...cache, items: cache.items.filter((o) => o.id !== deletedId) }
            : cache,
      );
      // The active list shouldn't contain the row (it was archived
      // before this call) but invalidate defensively in case the
      // user got here via a stale path.
      queryClient.setQueryData<{ items: OrganizationListItem[] }>(
        queryKeys.organizations.all(),
        (cache) =>
          cache
            ? { ...cache, items: cache.items.filter((o) => o.id !== deletedId) }
            : cache,
      );
      // Keep session projection in lockstep.
      syncOrgsFromCache(queryClient, syncOrganizations);
      queryClient.invalidateQueries({ queryKey: queryKeys.organizations.archived() });
    },
  });

  return {
    hardDeleteOrganization: mutation.mutateAsync,
    loading: mutation.isPending,
    error: mutation.error,
  };
}
